// Three rules from: pH / buffer pH are offered as **"Lime"** (never as themselves — a filing claims
// lime, not an acidity reading); **carbon and bulk density are NEVER offered**; and only P & K are
// available by default.
import type { SoilSamplePoint } from "../../types/contract";
import { sampleBlobToM3 } from "../../lib/soilLayers";
import { lbsPerPpmAt } from "../../lib/soilUnits";

export interface OfferedNutrient {
  key: string;
  label: string;
  /* * the compound a claim is filed in, when the platform knows it (extras name their own) */
  defaultProduct?: string;
}

/* * `lime` is the pH pair folded into one offer; the value accessor picks buffer pH first. */
export const LIME_KEY = "lime";

// Order is the offer order in the picker. P and K lead because they are the default claim.
export const OFFERABLE: OfferedNutrient[] = [
  { key: "p", label: "P", defaultProduct: "MAP" },
  { key: "k", label: "K", defaultProduct: "Potash" },
  { key: LIME_KEY, label: "Lime" },
  { key: "ca", label: "Calcium (Ca)" },
  { key: "mg", label: "Magnesium (Mg)" },
  { key: "s", label: "Sulfur (S)" },
  { key: "zn", label: "Zinc (Zn)" },
  { key: "mn", label: "Manganese (Mn)" },
  { key: "b", label: "Boron (B)" },
  { key: "cu", label: "Copper (Cu)" },
  { key: "fe", label: "Iron (Fe)" },
  { key: "no3_n", label: "Nitrate-N" },
  { key: "nh4_n", label: "Ammonium-N" },
  { key: "na", label: "Sodium (Na)" },
  { key: "cl", label: "Chloride (Cl)" },
];

// NEVER offerable: carbon and bulk density.
export const NEVER_OFFERED = new Set([
  "organic_c_pct",
  "organic_c",
  "carbon",
  "bulk_density",
  "cec",
  "om",
]);

/* * The two claim nutrients that are always present and always checked. */
export const DEFAULT_NUTRIENTS = ["p", "k"];

/* * Dimensionless analytes are never on a mass basis, so the lbs/ac guard must not touch them. */
const DIMENSIONLESS = new Set(["ph", "buffer_ph", "cec", "om", LIME_KEY]);

/*
 *
 * everything else: the normalized M3/8-inch value when present, else the stored value — with the
 * MASS-BASIS guard (a `units='lbs_ac'` row is POUNDS; convert at the row's own depth, and refuse
 * rather than guess when the depth is unknown).
 */
export function nutrientValue(s: SoilSamplePoint, key: string): number | null {
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const raw = (s.properties ?? {}) as Record<string, unknown>;
  const raw8 = (s.properties_8in ?? {}) as Record<string, unknown>;
  if (key === LIME_KEY) {
    return (
      num(raw8.buffer_ph) ?? num(raw.buffer_ph) ?? num(raw8.ph) ?? num(raw.ph)
    );
  }
  const m3 = sampleBlobToM3(s);
  const normalized = num(m3?.[key]) ?? num(raw8[key]);
  if (normalized != null) return normalized;
  const stored = num(raw[key]);
  if (stored == null || DIMENSIONLESS.has(key) || s.units !== "lbs_ac")
    return stored;
  return Number.isFinite(s.depth_in) && s.depth_in > 0
    ? stored / lbsPerPpmAt(s.depth_in)
    : null;
}

/* * Which offerable nutrients the selection's samples actually carry a value for. */
export function availableNutrients(
  sampleSets: SoilSamplePoint[][],
): OfferedNutrient[] {
  const samples = sampleSets.flat();
  return OFFERABLE.filter((n) => {
    if (NEVER_OFFERED.has(n.key)) return false;
    if (DEFAULT_NUTRIENTS.includes(n.key)) return true; // P and K are the claim; always offered
    return samples.some((s) => nutrientValue(s, n.key) != null);
  });
}

/* * The unit a nutrient's soil-test value is read in — lime is a pH reading, the rest are ppm. */
export const nutrientUnit = (key: string): string =>
  key === LIME_KEY ? "pH" : "ppm";
